import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { cn } from '@/lib/utils'
import { formatMonthYear } from '@/lib/format'
import TechnologyList from '@/components/projects/technology-list'
import TextLink from '@/components/elements/text-link'

interface ProjectMetadataProps {
    project: ProjectSummary
    readingTime?: number
    className?: string
}

const Row: FC<{ term: string, children: ReactNode }> = ({ term, children }): ReactNode => (
    <div className='grid grid-cols-[7.5rem_1fr] gap-4 border-t border-border py-3.5 first:border-t-0 sm:grid-cols-[9rem_1fr]'>
        <dt className='font-mono text-label uppercase text-muted-foreground'>{term}</dt>
        <dd className='min-w-0 text-[0.9375rem]'>{children}</dd>
    </div>
)

/** The case-study fact sheet: date, category, stack and links. */
const ProjectMetadata: FC<ProjectMetadataProps> = ({ project, readingTime, className }): ReactNode => {
    return (
        <dl className={cn('border-y border-foreground', className)}>
            <Row term='Category'>{project.categoryLabel}</Row>

            <Row term='Date'>
                <time dateTime={project.date}>{formatMonthYear(project.date)}</time>
            </Row>

            <Row term='Stack'>
                <TechnologyList items={project.technologies} className='text-[0.8125rem]' />
            </Row>

            <Row term='Links'>
                {project.demoUrl || project.sourceUrl ? (
                    <span className='flex flex-wrap gap-x-5 gap-y-2'>
                        {project.demoUrl && <TextLink href={project.demoUrl} arrow>Live demo</TextLink>}
                        {project.sourceUrl && <TextLink href={project.sourceUrl} arrow>Source code</TextLink>}
                    </span>
                ) : (
                    <span className='text-muted-foreground'>Not public yet</span>
                )}
            </Row>

            {readingTime !== undefined && (
                <Row term='Read'>{readingTime} min</Row>
            )}
        </dl>
    )
}

export default ProjectMetadata
